import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
// MapLibre is loaded from CDN via index.html to avoid minification issues
const maplibre = window.maplibregl;
import axios from 'axios';
import { io } from 'socket.io-client';
import { Box, Button, Card, CardContent, Typography, Modal, IconButton, CircularProgress } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import StoreIcon from '@mui/icons-material/Store';
import AddClearanceEvent from './AddClearanceEvent';
import EventSheet from '../pages/EventSheet';
import { getApiUrl } from '../apiUrl';

const StoreMap = forwardRef(({ userLocation, mapZoom, mapCenter, notificationRadius = 10, onProfileRefresh }, ref) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [storesLoading, setStoresLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(null); // { message, isInitialLoad, showLongMessage }
  const eventMarkersRef = useRef([]);
  const userLocationMarkerRef = useRef(null);
  const [isZoomedOutTooFar, setIsZoomedOutTooFar] = useState(false);
  const abortControllerRef = useRef(null);
  const isLoadingRef = useRef(false);
  const lastLoadBoundsRef = useRef(null);
  const loadingStartTimeRef = useRef(null);
  const longMessageTimeoutRef = useRef(null);
  const initialLoadCompleteRef = useRef(false);
  const dashboardStoresLoadedRef = useRef(false);

  const API_URL = getApiUrl();
  const socketRef = useRef(null);

  // Socket.io connection for real-time updates
  useEffect(() => {
    socketRef.current = io(API_URL, {
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('new-event', (eventData) => {
      console.log('New event received:', eventData);
      // Add the new event marker without reloading stores or jumping the map
      if (map.current && eventData.storeId && eventData.storeId.location) {
        const [lng, lat] = eventData.storeId.location.coordinates;
        // Just add this one event marker, don't reload everything
        addEventMarkers([eventData]);
        setEvents(prev => {
          // Add to existing events if not already there
          const exists = prev.some(e => e._id?.toString() === eventData._id?.toString());
          if (!exists) {
            return [...prev, eventData].slice(0, 100);
          }
          return prev;
        });
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (map.current) return;

    const defaultCenter = mapCenter || userLocation || [-93.2650, 36.6439]; // Default to Branson, MO
    const defaultZoom = mapZoom || 12;

    map.current = new maplibre.Map({
      container: mapContainer.current,
      style: 'https://api.maptiler.com/maps/5e93952d-a638-4302-8ab2-bab246582ff4/style.json?key=UHHUx3iqEIPjCVMQT4MD',
      center: defaultCenter,
      zoom: defaultZoom
    });

    map.current.on('load', () => {
      // Map loaded, loading stores...
      
      // Hide default POI layers (hotels, restaurants, etc.) so only our retail markers show
      try {
        const layers = map.current.getStyle().layers;
        layers.forEach(layer => {
          const layerId = layer.id.toLowerCase();
          // Hide all POI layers to prevent default markers from showing
          if (layerId.includes('poi') || 
              layerId.includes('symbol') ||
              layerId.includes('label') ||
              layerId.includes('point') ||
              layerId.includes('marker')) {
            try {
              // Only hide if it's not a road or building label
              if (!layerId.includes('road') && 
                  !layerId.includes('building') && 
                  !layerId.includes('place') &&
                  !layerId.includes('city') &&
                  !layerId.includes('country')) {
                map.current.setLayoutProperty(layer.id, 'visibility', 'none');
              }
            } catch (e) {
              // Layer might not support visibility property
            }
          }
        });
      } catch (e) {
        console.log('Could not hide POI layers:', e);
      }
      
      // Small delay to ensure map is fully ready
      setTimeout(() => {
        if (userLocation || mapCenter) {
          const center = userLocation || mapCenter;
          // Initial load: center on user's mapCenter and load 200 closest stores
          map.current.flyTo({
            center: center,
            zoom: mapZoom || 12,
            duration: 1000
          });
          
          // Wait for flyTo to complete, then load 200 stores closest to signup mapCenter
          setTimeout(() => {
            loadDashboardStores();
          }, 1100);
        } else {
          // Fallback if no location set
          loadDashboardStores();
        }
      }, 500);
    });

    // Load stores on initial load
    if ((userLocation || mapCenter) && map.current) {
      const timeoutId = setTimeout(() => {
        if (map.current && map.current.loaded()) {
          loadDashboardStores(); // Initial load: 200 stores closest to signup mapCenter
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [userLocation]);

  // Load closest stores within 3 miles when user location is obtained
  const loadStoresNearUserLocation = async (location) => {
    if (!map.current || !map.current.loaded()) return;
    
    const [lng, lat] = location;
    if (isNaN(lng) || isNaN(lat)) return;
    
    setStoresLoading(true);
    try {
      // Load closest 10 stores within 3 miles
      const response = await axios.get(`${API_URL}/api/stores/nearby`, {
        params: { lat, lng, radius: 3, limit: 10 },
        timeout: 30000
      });
      
      if (response.data && response.data.length > 0) {
        setStores(response.data);
        addStoreMarkers(response.data);
        
        // Load events near user's location
        loadEventsNearUser(location, 100);
      }
    } catch (error) {
      console.error('Error loading stores near user location:', error);
    } finally {
      setStoresLoading(false);
    }
  };

  // Share GPS: show blue pin only — never replace troop store pins or fly away from troop
  useEffect(() => {
    if (!map.current || !map.current.loaded()) return undefined;
    if (!userLocation || !Array.isArray(userLocation) || userLocation.length !== 2) {
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.remove();
        userLocationMarkerRef.current = null;
      }
      return undefined;
    }

    const [lng, lat] = userLocation;
    if (isNaN(lng) || isNaN(lat)) return undefined;

    if (userLocationMarkerRef.current) {
      userLocationMarkerRef.current.remove();
      userLocationMarkerRef.current = null;
    }

    userLocationMarkerRef.current = new maplibre.Marker({
      color: '#2196F3',
      scale: 1.2
    })
      .setLngLat([lng, lat])
      .addTo(map.current);

    return () => {
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.remove();
        userLocationMarkerRef.current = null;
      }
    };
  }, [userLocation]);

  // Pan/zoom: refresh events only — never reload troop stores after initial load
  useEffect(() => {
    if (!map.current || !map.current.loaded()) return;

    let panZoomTimeout = null;
    const debounceDelay = 1500;

    const handleMapMove = () => {
      if (!initialLoadCompleteRef.current) return;
      if (panZoomTimeout) clearTimeout(panZoomTimeout);
      panZoomTimeout = setTimeout(() => {
        if (!map.current) return;
        // After troop stores loaded once, only optionally refresh events when zoomed out
        if (!dashboardStoresLoadedRef.current) return;
        const zoom = map.current.getZoom();
        const bounds = map.current.getBounds();
        if (zoom < 6) {
          setIsZoomedOutTooFar(true);
          return;
        }
        setIsZoomedOutTooFar(false);
        if (zoom < 8) {
          loadActiveEvents(bounds, 50);
        }
      }, debounceDelay);
    };

    map.current.on('moveend', handleMapMove);
    map.current.on('zoomend', handleMapMove);

    return () => {
      if (panZoomTimeout) clearTimeout(panZoomTimeout);
      if (map.current) {
        map.current.off('moveend', handleMapMove);
        map.current.off('zoomend', handleMapMove);
      }
    };
  }, [map.current, userLocation, notificationRadius]);

  // Calculate radius from visible map bounds to target ~200 stores
  const calculateRadiusFromBounds = (bounds) => {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const centerLat = (ne.lat + sw.lat) / 2;
    
    const latDiff = Math.abs(ne.lat - sw.lat);
    const lngDiff = Math.abs(ne.lng - sw.lng);
    
    // Convert to miles (approximate)
    const latMiles = latDiff * 69;
    const lngMiles = lngDiff * 69 * Math.cos((centerLat * Math.PI) / 180);
    
    // Use the larger dimension to ensure we cover the visible area
    return Math.max(latMiles, lngMiles) * 0.6; // 60% of visible area to target ~150 stores
  };

  // Load stores within a specific radius (for panning within notification radius)
  // Load 200 stores closest to user's signup mapCenter (for dashboard initial load)
  const loadDashboardStores = async () => {
    if (!map.current || !map.current.loaded()) return;
    
    // Skip if we already have stores loaded (prevents repeated calls within same session)
    if (dashboardStoresLoadedRef.current || stores.length > 0) {
      if (dashboardStoresLoadedRef.current) {
        console.log('Dashboard stores already loaded, skipping API call');
      }
      return;
    }
    
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Prevent concurrent loads
    if (isLoadingRef.current) {
      return;
    }
    
    isLoadingRef.current = true;
    abortControllerRef.current = new AbortController();
    setStoresLoading(true);
    setLoadingProgress({ 
      message: 'Loading stores...', 
      isInitialLoad: true,
      showLongMessage: false
    });
    
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stores`, {
        timeout: 60000,
        signal: abortControllerRef.current.signal
      });

      if (response.data && response.data.length > 0) {
        // Normalize event counts from API (supports both field names)
        const normalized = response.data.map((s) => ({
          ...s,
          activeEventsCount: s.activeEventsCount ?? s.activeClearanceEvents ?? 0
        }));
        setStores(normalized);
        addStoreMarkers(normalized);
        fitMapToStores(normalized);
        
        // Log GPS coordinates for testing
        const coords = normalized.slice(0, 10).map(store => {
          const [lng, lat] = store.location.coordinates;
          return `${lng}, ${lat}`;
        });
        console.log('📍 GPS Coordinates (copy/paste for testing):', coords.join(' | '));
        
        // Mark that we've loaded dashboard stores (prevents repeated calls on refresh)
        dashboardStoresLoadedRef.current = true;
        
        // Mark initial load as complete - now pan/zoom handlers can work
        initialLoadCompleteRef.current = true;

        if (map.current) {
          const c = map.current.getCenter();
          lastLoadBoundsRef.current = { center: { lng: c.lng, lat: c.lat }, zoom: map.current.getZoom() };
        }
        
        // Load events near user's location
        if (userLocation) {
          loadEventsNearUser(userLocation, 100);
        }
      } else {
        // No stores yet — still show troop-centered map
        dashboardStoresLoadedRef.current = true;
        initialLoadCompleteRef.current = true;
        if (map.current) {
          const c = map.current.getCenter();
          lastLoadBoundsRef.current = { center: { lng: c.lng, lat: c.lat }, zoom: map.current.getZoom() };
        }
        fitMapToTroopRadius();
      }
      
      setLoadingProgress(null);
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error('Error loading dashboard stores:', error);
      setLoadingProgress(null);
    } finally {
      setStoresLoading(false);
      isLoadingRef.current = false;
    }
  };

  const loadStoresWithinRadius = async (center, radius) => {
    // Troop stores are loaded once; pan must not refetch or flash loading overlay
    if (dashboardStoresLoadedRef.current) return;
    if (!map.current || !map.current.loaded()) return;
    
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Prevent concurrent loads
    if (isLoadingRef.current) {
      return;
    }
    
    isLoadingRef.current = true;
    abortControllerRef.current = new AbortController();
    setStoresLoading(true);
    
    try {
      const [lng, lat] = center;
      
      // Load stores within radius (no cap, server handles efficiency)
      const response = await axios.get(`${API_URL}/api/stores`, {
        params: { lat, lng, radius: radius, limit: 10000 }, // High limit, server will handle
        timeout: 15000,
        signal: abortControllerRef.current.signal
      });

      if (response.data.length > 0) {
        // Replace stores with all stores within radius (for panning within notificationRadius)
        setStores(response.data);
        addStoreMarkers(response.data);
        
        // Add markers for new stores only
        const existingIds = new Set(stores.map(s => s._id?.toString()));
        const newStores = response.data.filter(s => !existingIds.has(s._id?.toString()));
        if (newStores.length > 0) {
          addStoreMarkers(newStores);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error('Error loading stores:', error);
    } finally {
      setStoresLoading(false);
      isLoadingRef.current = false;
      abortControllerRef.current = null;
    }
  };

  // Load events near a location
  // If withinNotificationRadius = true: Load ALL events within notification radius (no cap)
  // If withinNotificationRadius = false: Load up to 50 events outside notification radius
  const loadEventsNearUser = async (location, limit = 50, outsideNotificationRadius = false) => {
    if (!map.current || !map.current.loaded()) return;
    
    const [lng, lat] = location;
    
    try {
      let response;
      
      if (outsideNotificationRadius) {
        // Outside notification radius: load up to 50 events (up to 50 miles)
        response = await axios.get(`${API_URL}/api/events`, {
          params: {
            lat: lat,
            lng: lng,
            radius: 50, // 50 miles max
            limit: 50 // Max 50 events
          },
          timeout: 15000
        });
      } else if (userLocation && notificationRadius) {
        // Within notification radius: load ALL events (up to 9000)
        response = await axios.get(`${API_URL}/api/events`, {
          params: {
            lat: userLocation[1], // Use user's location, not map center
            lng: userLocation[0],
            radius: notificationRadius,
            limit: 9000 // Up to 9000 events
          },
          timeout: 15000
        });
      } else {
        // No notification radius: load from map center
        response = await axios.get(`${API_URL}/api/events`, {
          params: {
            lat: lat,
            lng: lng,
            radius: 25,
            limit: limit
          },
          timeout: 15000
        });
      }

      if (response.data.length > 0) {
        // Add event markers without clearing store markers
        addEventMarkers(response.data);
        setEvents(prev => {
          // Merge with existing events, avoiding duplicates
          const existingIds = new Set(prev.map(e => e._id?.toString()));
          const newEvents = response.data.filter(e => !existingIds.has(e._id?.toString()));
          if (outsideNotificationRadius) {
            // Limit to 50 for outside events
            return [...prev, ...newEvents].slice(0, 50);
          } else {
            // No limit for events within notification radius
            return [...prev, ...newEvents];
          }
        });
      }
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  // Load active events by map bounds (for zoomed-out view)
  const loadActiveEvents = async (bounds, limit = 50) => {
    if (!map.current || !map.current.loaded()) return;

    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Prevent concurrent loads
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    abortControllerRef.current = new AbortController();
    setEventsLoading(true);
    try {
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      
      const response = await axios.get(`${API_URL}/api/events/bounds`, {
        params: {
          north: ne.lat,
          south: sw.lat,
          east: ne.lng,
          west: sw.lng,
          limit: limit
        },
        timeout: 15000,
        signal: abortControllerRef.current.signal
      });

      if (response.data.length > 0) {
        // Limit to 50 events for performance
        const limitedEvents = response.data.slice(0, limit);
        setEvents(limitedEvents);
        // Clear store markers and add event markers
        clearStoreMarkers();
        addEventMarkers(limitedEvents);
      } else {
        setEvents([]);
        clearEventMarkers();
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Error loading active events:', error);
      setEvents([]);
      clearEventMarkers();
    } finally {
      setEventsLoading(false);
      isLoadingRef.current = false;
      abortControllerRef.current = null;
    }
  };

  // Fit map to show all stores
  const fitMapToStores = (storesToFit) => {
    if (!map.current || !storesToFit || storesToFit.length === 0) {
      fitMapToTroopRadius();
      return;
    }

    const coordinates = storesToFit
      .filter(store => store.location && store.location.coordinates)
      .map(store => store.location.coordinates);

    if (coordinates.length === 0) {
      fitMapToTroopRadius();
      return;
    }

    // Prefer troop/map center in bounds so zone stays framed
    const center = mapCenter || userLocation;
    if (center) {
      coordinates.push(center);
    }

    // Calculate bounds
    const lngs = coordinates.map(coord => coord[0]);
    const lats = coordinates.map(coord => coord[1]);
    
    const bounds = [
      [Math.min(...lngs), Math.min(...lats)], // Southwest
      [Math.max(...lngs), Math.max(...lats)]  // Northeast
    ];

    // Add padding (10% on each side)
    const padding = 0.1;
    const lngDiff = bounds[1][0] - bounds[0][0];
    const latDiff = bounds[1][1] - bounds[0][1];
    
    bounds[0][0] -= lngDiff * padding;
    bounds[0][1] -= latDiff * padding;
    bounds[1][0] += lngDiff * padding;
    bounds[1][1] += latDiff * padding;

    // Fit map to bounds
    map.current.fitBounds(bounds, {
      padding: 50, // pixels
      duration: 1000 // animation duration
    });
  };

  const fitMapToTroopRadius = () => {
    if (!map.current) return;
    const center = mapCenter || userLocation;
    if (!center || center.length !== 2) return;
    const miles = Number(notificationRadius) || 10;
    const lat = center[1];
    const lng = center[0];
    const latDelta = miles / 69;
    const lngDelta = miles / (69 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
    map.current.fitBounds(
      [
        [lng - lngDelta, lat - latDelta],
        [lng + lngDelta, lat + latDelta]
      ],
      { padding: 48, duration: 800, maxZoom: 13 }
    );
  };

  // Create sample stores for testing
  const createSampleStores = () => {
    const sampleStores = [
      {
        _id: '1',
        name: 'Walmart Supercenter',
        address: { fullAddress: '123 Main St, Branson, MO 65616' },
        location: { coordinates: [-93.2650, 36.6439] },
        category: 'retail',
        brandColor: '#0071CE', // Walmart blue
        activeEventsCount: 0
      },
      {
        _id: '2',
        name: 'Target',
        address: { fullAddress: '456 Commerce Dr, Branson, MO 65616' },
        location: { coordinates: [-93.2700, 36.6500] },
        category: 'retail',
        brandColor: '#CC0000', // Target red
        activeEventsCount: 0
      },
      {
        _id: '3',
        name: 'Best Buy',
        address: { fullAddress: '789 Tech Blvd, Branson, MO 65616' },
        location: { coordinates: [-93.2600, 36.6400] },
        category: 'electronics',
        brandColor: '#003B64', // Best Buy blue
        activeEventsCount: 0
      }
    ];

    setStores(sampleStores);
    addStoreMarkers(sampleStores);
  };

  // Convert a color to a grey version (desaturated)
  const convertToGrey = (color) => {
    // If it's already a hex color, convert to grey
    if (color.startsWith('#')) {
      // Parse hex color
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Convert to grayscale using luminance formula
      const grey = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
      
      // Return as a slightly lighter grey to maintain some visibility
      const greyValue = Math.max(150, grey); // Keep it visible but grey
      return `#${greyValue.toString(16).padStart(2, '0').repeat(3)}`;
    }
    return '#9CA3AF'; // Default grey if parsing fails
  };

  // Helper function to calculate distance between two coordinates in meters
  const calculateDistance = (coord1, coord2) => {
    const [lng1, lat1] = coord1;
    const [lng2, lat2] = coord2;
    const R = 6371000; // Earth radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Helper function to calculate adjusted positions for overlapping active markers
  const adjustMarkerPositions = (stores, currentZoom) => {
    // Only adjust active (clickable) markers
    const activeStores = stores.filter(s => s.activeEventsCount > 0);
    if (activeStores.length < 2) {
      return new Map(); // No need to adjust if less than 2 active markers
    }
    
    const adjustedPositions = new Map();
    
    // Marker size in pixels (active pins are 30px)
    const markerSize = 30;
    // Minimum separation in pixels - want at least 50% non-overlap
    const minSeparationPx = markerSize * 0.6; // ~18px separation (60% of marker size)
    
    // Convert pixel distance to meters based on zoom level
    // Formula: metersPerPixel = (156543.03392 * Math.cos(lat * Math.PI / 180)) / (2^zoom)
    const avgLat = stores.length > 0 ? stores[0].location.coordinates[1] : 0;
    const metersPerPixel = (156543.03392 * Math.cos(avgLat * Math.PI / 180)) / Math.pow(2, currentZoom);
    const minSeparationMeters = minSeparationPx * metersPerPixel;
    
    // Initialize all active stores with their original positions
    activeStores.forEach(store => {
      adjustedPositions.set(store._id, [...store.location.coordinates]);
    });
    
    // Iterate through pairs and adjust positions
    // Use multiple passes for better convergence
    for (let pass = 0; pass < 3; pass++) {
      for (let i = 0; i < activeStores.length; i++) {
        const store1 = activeStores[i];
        const pos1 = adjustedPositions.get(store1._id);
        
        for (let j = i + 1; j < activeStores.length; j++) {
          const store2 = activeStores[j];
          const pos2 = adjustedPositions.get(store2._id);
          
          const distance = calculateDistance(pos1, pos2);
          
          // If markers are too close (would overlap more than 50%)
          if (distance < minSeparationMeters) {
            const [lng1, lat1] = pos1;
            const [lng2, lat2] = pos2;
            
            // Vector from store1 to store2
            const dx = lng2 - lng1;
            const dy = lat2 - lat1;
            
            // Calculate how much we need to push them apart
            const pushDistance = (minSeparationMeters - distance) / 2;
            
            // Normalize the direction vector
            const length = Math.sqrt(dx * dx + dy * dy);
            if (length > 0) {
              const normalizedX = dx / length;
              const normalizedY = dy / length;
              
              // Convert push distance to degrees
              const latOffset = (pushDistance / 111320) * normalizedY;
              const lngOffset = (pushDistance / (111320 * Math.cos(avgLat * Math.PI / 180))) * normalizedX;
              
              // Push store1 away from store2 (subtract offset)
              pos1[0] -= lngOffset;
              pos1[1] -= latOffset;
              
              // Push store2 away from store1 (add offset)
              pos2[0] += lngOffset;
              pos2[1] += latOffset;
            }
          }
        }
      }
    }
    
    return adjustedPositions;
  };

  // Add store markers to map
  const addStoreMarkers = (storeList) => {
    if (!map.current) {
      console.error('Cannot add markers: map not initialized');
      return;
    }

    if (!storeList || storeList.length === 0) {
      return;
    }
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Get current zoom level for collision detection
    const currentZoom = map.current.getZoom();

    // Calculate adjusted positions for overlapping active markers
    const adjustedPositions = adjustMarkerPositions(storeList, currentZoom);

      // Sort stores: clickable ones LAST so they render on top (DOM order matters)
      const sortedStores = [...storeList].sort((a, b) => {
      const aClickable = a.activeEventsCount > 0;
      const bClickable = b.activeEventsCount > 0;
      if (aClickable && !bClickable) return 1; // Clickable ones go to end
      if (!aClickable && bClickable) return -1; // Non-clickable ones go first
      return 0;
    });

    sortedStores.forEach((store, index) => {
      // Use adjusted position if available, otherwise use original
      const originalCoords = store.location.coordinates;
      const adjustedCoords = adjustedPositions.get(store._id) || originalCoords;
      const [lng, lat] = adjustedCoords;
      
      if (!lng || !lat || isNaN(lng) || isNaN(lat)) {
        console.warn(`Invalid coordinates for store ${store.name}:`, originalCoords);
        return;
      }
      
      // Get brand color or use default
      const baseBrandColor = store.brandColor || '#6B7280';
      const hasEvents = (store.activeEventsCount || store.activeClearanceEvents || 0) > 0;
      
      // Make clickable only if has active events
      const isClickable = hasEvents;
      
      // Determine final color based on clickable status
      let brandColor;
      if (isClickable) {
        // If clickable and has a brand color, use full color
        // If clickable but no brand color (grey default), use light blue
        if (baseBrandColor === '#6B7280') {
          brandColor = '#ADD8E6'; // Light blue for clickable stores without brand color
        } else {
          brandColor = baseBrandColor; // Full brand color for clickable stores
        }
      } else {
        // If not clickable, use grey version of the color
        if (baseBrandColor === '#6B7280') {
          brandColor = '#6B7280'; // Already grey, keep it
        } else {
          // Convert color to grey version
          brandColor = convertToGrey(baseBrandColor);
        }
      }
      
      // Adding marker: ${store.name}
      
      // Create custom marker element - simple circular marker with color
      const el = document.createElement('div');
      el.className = 'store-marker';
      
      // Much bigger pins for clickable stores, smaller for grey ones
      const pinSize = isClickable ? 30 : 16;
      
      // Set styles directly on the element - this is the actual marker
      // MapLibre will handle positioning, so we just style the element itself
      el.style.width = `${pinSize}px`;
      el.style.height = `${pinSize}px`;
      el.style.backgroundColor = brandColor; // THIS IS THE KEY - set color directly
      el.style.border = '3px solid white';
      el.style.borderRadius = '50%';
      el.style.boxShadow = '0 3px 8px rgba(0,0,0,0.4)';
      el.style.cursor = isClickable ? 'pointer' : 'default';
      // Set z-index directly - MapLibre markers support this
      // Active pins (with events) should be above inactive pins, but ALL pins should be below modals
      if (isClickable) {
        el.style.zIndex = '100'; // Active pins above inactive (which default to lower)
      } else {
        el.style.zIndex = '50'; // Inactive pins lower
      }
      
      // Add inner white dot for visibility
      const innerDot = document.createElement('div');
      innerDot.style.position = 'absolute';
      innerDot.style.top = '50%';
      innerDot.style.left = '50%';
      innerDot.style.transform = 'translate(-50%, -50%)';
      innerDot.style.width = `${pinSize * 0.4}px`;
      innerDot.style.height = `${pinSize * 0.4}px`;
      innerDot.style.backgroundColor = 'white';
      innerDot.style.borderRadius = '50%';
      innerDot.style.pointerEvents = 'none';
      el.appendChild(innerDot);
      
      // Add pulse effect for stores with events
      if (hasEvents) {
        const pulse = document.createElement('div');
        pulse.style.position = 'absolute';
        pulse.style.top = '50%';
        pulse.style.left = '50%';
        pulse.style.transform = 'translate(-50%, -50%)';
        pulse.style.width = '100%';
        pulse.style.height = '100%';
        pulse.style.borderRadius = '50%';
        pulse.style.backgroundColor = brandColor;
        pulse.style.animation = 'pulse 2s infinite';
        pulse.style.opacity = '0.6';
        pulse.style.pointerEvents = 'none';
        el.appendChild(pulse);
      }
      
      // Force the color to be set - use setAttribute as backup
      el.setAttribute('data-color', brandColor);
      el.setAttribute('data-store-name', store.name);
      
      // Debug: verify color is set (commented out to reduce console spam)
      // console.log(`Marker element created for ${store.name}:`, {
      //   backgroundColor: el.style.backgroundColor,
      //   computed: brandColor,
      //   element: el
      // });

      // Hover effects for clickable markers - use box-shadow and opacity instead of transform to avoid position shift
      if (isClickable) {
        el.addEventListener('mouseenter', () => {
          el.style.boxShadow = '0 5px 15px rgba(0,0,0,0.6)';
          el.style.opacity = '0.9';
          el.style.zIndex = '101'; // Slightly higher on hover, but still below modals
        });
        
        el.addEventListener('mouseleave', () => {
          el.style.boxShadow = '0 3px 8px rgba(0,0,0,0.4)';
          el.style.opacity = '1';
          el.style.zIndex = '100'; // Back to normal active pin z-index
        });
      }

      // Create marker - use 'center' anchor for circular markers so center aligns with coordinates
      const marker = new maplibre.Marker({
        element: el,
        anchor: 'center'
      })
        .setLngLat([lng, lat])
        .addTo(map.current);
      
      // Force clickable markers to appear on top by manipulating the DOM order
      if (isClickable) {
        // Move the marker element to the end of its parent to ensure it's on top
        setTimeout(() => {
          const markerElement = el.parentElement;
          if (markerElement && markerElement.parentElement) {
            markerElement.parentElement.appendChild(markerElement);
          }
        }, 0);
      }
      
      // Store reference to marker for potential cleanup
      marker._storeData = { name: store.name, isClickable, brandColor };

      // Marker color check removed to reduce console spam

      // Add click handler for clickable markers - prevent map from handling the click
      if (isClickable) {
        const handleClick = async (e) => {
          e.stopPropagation();
          e.preventDefault();
          console.log('Marker clicked:', store.name);
          setSelectedStore(store);
          if (store._id) {
            await loadStoreEvents(store._id, store);
          }
        };
        
        // Handle both mouse and touch events
        el.addEventListener('click', handleClick);
        el.addEventListener('touchend', (e) => {
          e.stopPropagation();
          e.preventDefault();
          handleClick(e);
        });
        
        // Prevent map drag when interacting with marker
        el.addEventListener('mousedown', (e) => {
          e.stopPropagation();
        });
        
        el.addEventListener('touchstart', (e) => {
          e.stopPropagation();
        });
      }

      // Add tooltip on hover
      el.title = `${store.name}${hasEvents ? ` (${store.activeEventsCount} event${store.activeEventsCount !== 1 ? 's' : ''})` : ''}`;

      markersRef.current.push(marker);
    });
    
    // Successfully added markers to map
  };

  // Clear store markers
  const clearStoreMarkers = () => {
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
  };

  // Clear event markers
  const clearEventMarkers = () => {
    eventMarkersRef.current.forEach(marker => marker.remove());
    eventMarkersRef.current = [];
  };

  // Add event markers to map (white/colored pins for active events)
  const addEventMarkers = (eventList) => {
    if (!map.current) return;

    if (!eventList || eventList.length === 0) return;
    
    // Don't clear existing markers - just add new ones (avoid duplicates by checking existing)
    const existingEventIds = new Set(eventMarkersRef.current.map(m => m._eventId));

    const newEventMarkers = [];

    eventList.forEach((event) => {
      if (!event.storeId || !event.storeId.location || !event.storeId.location.coordinates) return;
      
      // Skip if this event marker already exists
      if (existingEventIds.has(event._id?.toString())) return;

      const [lng, lat] = event.storeId.location.coordinates;
      
      // Create white marker with colored border for events
      const el = document.createElement('div');
      el.className = 'event-marker';
      
      const pinSize = 24; // Medium size for events
      
      el.style.width = `${pinSize}px`;
      el.style.height = `${pinSize}px`;
      el.style.backgroundColor = '#FFFFFF'; // White background
      el.style.border = '3px solid #331B65'; // Red border (or use event color)
      el.style.borderRadius = '50%';
      el.style.boxShadow = '0 3px 8px rgba(0,0,0,0.4)';
      el.style.cursor = 'pointer';
      el.style.zIndex = '90'; // Below store markers but visible
      
      // Add inner colored dot
      const innerDot = document.createElement('div');
      innerDot.style.position = 'absolute';
      innerDot.style.top = '50%';
      innerDot.style.left = '50%';
      innerDot.style.transform = 'translate(-50%, -50%)';
      innerDot.style.width = `${pinSize * 0.5}px`;
      innerDot.style.height = `${pinSize * 0.5}px`;
      innerDot.style.backgroundColor = '#331B65'; // Red dot
      innerDot.style.borderRadius = '50%';
      innerDot.style.pointerEvents = 'none';
      el.appendChild(innerDot);

      // Hover effect
      el.addEventListener('mouseenter', () => {
        el.style.boxShadow = '0 5px 15px rgba(0,0,0,0.6)';
        el.style.transform = 'scale(1.1)';
      });
      
      el.addEventListener('mouseleave', () => {
        el.style.boxShadow = '0 3px 8px rgba(0,0,0,0.4)';
        el.style.transform = 'scale(1)';
      });

      // Click handler - open event sheet
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        setSelectedEvent(event);
      });

      const marker = new maplibre.Marker({
        element: el,
        anchor: 'center'
      })
        .setLngLat([lng, lat])
        .addTo(map.current);
      
      // Store event ID on marker for duplicate checking
      marker._eventId = event._id?.toString();
      newEventMarkers.push(marker);
    });

    // Add new markers to existing ones (don't replace)
    eventMarkersRef.current = [...eventMarkersRef.current, ...newEventMarkers];
  };

  // Load events for a store
  const loadStoreEvents = async (storeId, store = null) => {
    if (!storeId) {
      setEvents([]);
      setEventsLoading(false);
      return;
    }
    setEventsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/stores/${storeId}/events`);
      const loadedEvents = response.data;
      setEvents(loadedEvents);
      // Auto-select first event if we have events
      // Use the passed store parameter or selectedStore state
      const currentStore = store || selectedStore;
      if (currentStore && loadedEvents.length > 0) {
        // Set the store if it wasn't already set
        if (store && !selectedStore) {
          setSelectedStore(store);
        }
        setSelectedEvent(loadedEvents[0]);
      }
    } catch (error) {
      console.error('Error loading store events:', error);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  // Expose method to open AddClearanceEvent for a specific store
  useImperativeHandle(ref, () => ({
    openAddEventForStore: (store) => {
      setSelectedStore(store);
      setShowAddEvent(true);
      if (store._id) {
        loadStoreEvents(store._id, store);
      }
    }
  }));

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Loading indicator */}
      <AnimatePresence>
        {(storesLoading || loadingProgress) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute',
              top: 80,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              zIndex: 1000,
              pointerEvents: 'none',
              width: '100%',
              margin: 0,
              padding: 0
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'white',
                px: 3,
                py: 2,
                borderRadius: 2,
                boxShadow: 3,
                minWidth: '200px',
                pointerEvents: 'auto'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">
                  {loadingProgress?.message || 'Loading stores...'}
                </Typography>
              </Box>
              {loadingProgress?.showLongMessage && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key="fetching"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                      fetching updated store data... almost done...
                    </Typography>
                  </motion.div>
                </AnimatePresence>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {eventsLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'white',
            px: 3,
            py: 1.5,
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2">Loading events...</Typography>
        </Box>
      )}

      {/* "ZOOMED OUT TOO FAR" warning (like Roadie app) */}
      {isZoomedOutTooFar && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            bgcolor: '#D4A574', // Light brown like Roadie
            color: 'white',
            px: 3,
            py: 2,
            borderRadius: 2,
            boxShadow: 4,
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: '#C49564'
            }
          }}
          onClick={() => {
            if (map.current && userLocation) {
              map.current.flyTo({
                center: userLocation,
                zoom: 10,
                duration: 1000
              });
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            ⚠️ ZOOMED OUT TOO FAR
          </Typography>
          <Typography variant="body2">
            Tap to Zoom In
          </Typography>
        </Box>
      )}

      <style>{`
        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.6;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.5);
            opacity: 0;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.6;
          }
        }
        .store-marker {
          pointer-events: auto !important;
          display: block !important;
          visibility: visible !important;
        }
        /* Ensure clickable markers appear on top */
        .maplibregl-marker[style*="z-index: 10000"] {
          z-index: 10000 !important;
        }
      `}</style>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />

      {/* Store Info Card - Only show if store has no events (dead pin) and events are loaded */}
      {selectedStore && events.length === 0 && !eventsLoading && (
        <Card
          sx={{
            position: 'absolute',
            bottom: 20,
            left: 20,
            right: 20,
            maxWidth: 500,
            zIndex: 1000,
            maxHeight: '60vh',
            overflow: 'auto'
          }}
        >
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box flex={1}>
                <Typography variant="h6" gutterBottom>{selectedStore.name}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedStore.address?.fullAddress || 'Address not available'}
                </Typography>
                {selectedStore.phone && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {selectedStore.phone}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  No active clearance events
                </Typography>
              </Box>
              <IconButton 
                onClick={() => {
                  setSelectedStore(null);
                  setEvents([]);
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              onClick={() => setShowAddEvent(true)}
              sx={{ mb: 2, backgroundColor: '#202E40' }}
            >
              Post A Clearance Event
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Event Modal - Full screen on mobile */}
      <Modal 
        open={showAddEvent} 
        onClose={() => setShowAddEvent(false)}
        sx={{
          zIndex: 10002, // Above map markers (10000) but below EventSheet (10003)
          // Mobile-first: full screen on small devices
          '@media (max-width: 600px)': {
            '& .MuiModal-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              zIndex: 10001
            }
          }
        }}
        BackdropProps={{
          sx: { zIndex: 10001 }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 10002,
            // Desktop: centered modal
            '@media (min-width: 601px)': {
              top: '50%',
              left: '50%',
              right: 'auto',
              bottom: 'auto',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: 500,
              height: 'auto',
              maxHeight: '90vh',
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden'
            }
          }}
        >
          <AddClearanceEvent
            store={selectedStore}
            onClose={() => {
              setShowAddEvent(false);
            }}
            onEventCreated={async (createdEvent) => {
              setShowAddEvent(false);
              if (selectedStore && createdEvent) {
                // Bump local active count so pin becomes clickable/colored
                setStores((prev) => {
                  const next = prev.map((s) =>
                    s._id === selectedStore._id
                      ? {
                          ...s,
                          activeEventsCount: (s.activeEventsCount || 0) + 1,
                          activeClearanceEvents: (s.activeClearanceEvents || 0) + 1
                        }
                      : s
                  );
                  addStoreMarkers(next);
                  return next;
                });
                await loadStoreEvents(selectedStore._id, selectedStore);
                setSelectedEvent(createdEvent);
                setSelectedStore(selectedStore);
              }
            }}
          />
        </Box>
      </Modal>

      {/* Event Sheet - Full screen bottom sheet */}
      {selectedEvent && selectedStore && (
        <EventSheet
          store={selectedStore}
          event={selectedEvent}
          onClose={() => {
            setSelectedEvent(null);
            setSelectedStore(null);
            setEvents([]);
          }}
          onUpdate={async (maybeEvent) => {
            if (maybeEvent && maybeEvent._id) {
              setSelectedEvent(maybeEvent);
              return;
            }
            if (selectedEvent?._id) {
              try {
                const res = await axios.get(`${API_URL}/api/events/${selectedEvent._id}`);
                setSelectedEvent(res.data);
              } catch (err) {
                console.error('Refresh event failed', err);
                if (selectedStore) loadStoreEvents(selectedStore._id, selectedStore);
              }
            } else if (selectedStore) {
              loadStoreEvents(selectedStore._id, selectedStore);
            }
          }}
          onProfileRefresh={onProfileRefresh}
        />
      )}
    </Box>
  );
});

export default StoreMap;


